/**
 * Proyección mes a mes, la misma en todos los canales (épica product#14, chirigota#575).
 *
 * comparsa decide la tasa (`GET /plan-returns` → `monthlyReturnPct`, sin redondear) y aquí
 * solo se calcula la curva, para que la gráfica responda al instante con los sliders.
 * Aportación a FINAL de cada mes: `saldo = saldo × (1 + r_mes) + aportación`.
 * Los casos de prueba compartidos están en `monthlyProjection.cases.json` (copia de comparsa).
 *
 * - La tasa llega NETA de comisiones (dato DGSFP): no se vuelve a restar la comisión de gestión.
 * - Solo años enteros: con fracciones, comparsa y este fichero podrían contar los meses distinto.
 * - Ciclo de vida por edad (chirigota#592): comparsa manda los tramos (`lifecycle.schedule`) y
 *   aquí se encadenan, cada año con la tasa de su tramo. Ningún tramo se calcula en el front.
 */

/** Edad de jubilación con la que comparsa calcula los tramos (edad = 67 − años). */
const RETIREMENT_AGE = 67;

/**
 * Años hasta la jubilación de partida cuando no sabemos ni los años ni la edad de quien viene del
 * corte del funnel (product#76): la mediana de quienes firmaron un traspaso (product#76 §5).
 */
export const DEFAULT_YEARS_TO_RETIREMENT_UNKNOWN = 17;

/** Un tramo del ciclo de vida: años desde hoy [fromYear, toYear) con su tasa mensual. */
export interface RateStage {
	fromYear: number;
	toYear: number;
	monthlyReturnPct: number;
}

/** Tasa mensual de la curva: una sola, o los tramos del ciclo de vida. */
export type MonthlyRate = number | ReadonlyArray<RateStage>;

/** Años hasta la jubilación que admite comparsa (edad de 18 a 66). */
const MAX_YEARS_TO_RETIREMENT = RETIREMENT_AGE - 18;

/**
 * Años hasta la jubilación listos para comparsa (entero de 1 a 49), o `undefined` si no valen:
 * fuera de rango comparsa responde 400 y se perdería toda la respuesta, no solo los tramos.
 */
export function validYearsToRetirement(raw: unknown): number | undefined {
	if (raw === null || raw === undefined || raw === '') return undefined;
	const years = Math.round(Number(raw));
	return Number.isFinite(years) && years >= 1 && years <= MAX_YEARS_TO_RETIREMENT
		? years
		: undefined;
}

/** Años hasta la jubilación según su edad (de su fecha de nacimiento), o `undefined`. */
export function yearsToRetirementFromAge(age: number | undefined): number | undefined {
	if (age === undefined || !Number.isFinite(age) || age < 18 || age >= RETIREMENT_AGE) {
		return undefined;
	}
	return RETIREMENT_AGE - Math.floor(age);
}

/**
 * Años con los que pedir los tramos del ciclo de vida: los que dio en el funnel (los mismos con
 * los que se dibuja su comparativa) y, si no, los de su edad. Sin ninguno, el 50/50 de siempre.
 */
export function yearsToRetirementFor(
	onboardingYears: unknown,
	age: number | undefined
): number | undefined {
	return validYearsToRetirement(onboardingYears) ?? yearsToRetirementFromAge(age);
}

/** Anual equivalente a una mensual, en %. */
function annualFromMonthlyPct(monthlyPct: number): number {
	return (Math.pow(1 + monthlyPct / 100, 12) - 1) * 100;
}

/**
 * La misma tasa (o cada tramo) con `deltaPct` puntos más de rentabilidad anual: las bandas
 * favorable y desfavorable del simulador, que así siguen los tramos del ciclo de vida.
 */
export function shiftAnnualPct(rate: MonthlyRate, deltaPct: number): MonthlyRate {
	const shift = (monthlyPct: number) => monthlyRatePct(annualFromMonthlyPct(monthlyPct) + deltaPct);
	if (typeof rate === 'number') return shift(rate);
	return rate.map((stage) => ({ ...stage, monthlyReturnPct: shift(stage.monthlyReturnPct) }));
}

/** Tasa mensual del año `year` (0 = el primero). Tras el último tramo, la del último. */
function monthlyPctForYear(rate: MonthlyRate, year: number): number {
	if (typeof rate === 'number') return rate;
	if (rate.length === 0) throw new RangeError('Ciclo de vida sin tramos');
	const stage = rate.find((s) => year >= s.fromYear && year < s.toYear);
	return (stage ?? rate[rate.length - 1]).monthlyReturnPct;
}

function assertWholeYears(years: number): void {
	if (!Number.isInteger(years) || years < 0) {
		throw new RangeError(`La proyección va por años enteros (llegó ${years})`);
	}
}

/** `(1 + r_anual)^(1/12) − 1`, en %, sin redondear. */
export function monthlyRatePct(annualPct: number): number {
	return (Math.pow(1 + annualPct / 100, 1 / 12) - 1) * 100;
}

/**
 * Saldo tras `years` años enteros, con la tasa MENSUAL del endpoint (`monthlyReturnPct`) o los
 * tramos del ciclo de vida (`lifecycle.schedule`).
 */
export function projectMonthly(
	initial: number,
	monthlyContribution: number,
	rate: MonthlyRate,
	years: number
): number {
	assertWholeYears(years);
	let balance = initial;
	for (let year = 0; year < years; year++) {
		const monthly = monthlyPctForYear(rate, year) / 100;
		for (let month = 0; month < 12; month++) {
			balance = balance * (1 + monthly) + monthlyContribution;
		}
	}
	return balance;
}

/** Saldo al final de cada año, calculado mes a mes: índice 0 = hoy, índice N = año N. */
export function projectByYear(
	initial: number,
	monthlyContribution: number,
	rate: MonthlyRate,
	years: number
): number[] {
	assertWholeYears(years);
	const serie = [initial];
	let balance = initial;
	for (let year = 1; year <= years; year++) {
		balance = projectMonthly(balance, monthlyContribution, monthlyPctForYear(rate, year - 1), 1);
		serie.push(balance);
	}
	return serie;
}

/**
 * Dos planes con la misma aportación, en el formato de la gráfica de proyección
 * (`{ year, plan, bank }`, redondeado al euro). Con `otherMonthlyPct = 0`, `bank` es lo
 * aportado sin invertir. Las dos tasas, mensuales y netas (`monthlyReturnPct`), o sus tramos.
 */
export function projectComparisonByYear(
	initial: number,
	monthlyContribution: number,
	planMonthlyPct: MonthlyRate,
	otherMonthlyPct: MonthlyRate,
	years: number
): { year: number; plan: number; bank: number }[] {
	const plan = projectByYear(initial, monthlyContribution, planMonthlyPct, years);
	const other = projectByYear(initial, monthlyContribution, otherMonthlyPct, years);
	return plan.map((value, year) => ({
		year,
		plan: Math.round(value),
		bank: Math.round(other[year])
	}));
}
